import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { money, computeTotals, lineTotal, num } from "@/lib/format";
import {
  ArrowLeft, Plus, Trash2, Save, Cpu, Wand2, ShieldCheck, ChevronDown, Loader2,
  X, Sparkles, AlertTriangle, Info, CheckCircle2, Layers,
  FileSpreadsheet, CalendarRange, ShieldAlert, FileText, GitPullRequest, Gavel, ClipboardCheck, Ruler, Camera, FlaskConical, Database, Users, Blocks, Receipt, Replace, MapPin, Boxes, FileCheck2, Image as ImageIcon, Paperclip, BookMarked, TrendingUp, ScanLine,
} from "lucide-react";
import ScheduleTab from "@/components/tabs/ScheduleTab";
import RiskTab from "@/components/tabs/RiskTab";
import ProposalTab from "@/components/tabs/ProposalTab";
import ChangeOrdersTab from "@/components/tabs/ChangeOrdersTab";
import TenderingTab from "@/components/tabs/TenderingTab";
import QuoteReviewTab from "@/components/tabs/QuoteReviewTab";
import TakeoffTab from "@/components/tabs/TakeoffTab";
import ProgressTab from "@/components/tabs/ProgressTab";
import ScenariosTab from "@/components/tabs/ScenariosTab";
import RevisionsTab from "@/components/tabs/RevisionsTab";
import Design3DTab from "@/components/tabs/Design3DTab";
import CompliancePanel from "@/components/CompliancePanel";
import ProjectPhotos from "@/components/ProjectPhotos";
import BidReviewTab from "@/components/tabs/BidReviewTab";
import WinTab from "@/components/tabs/WinTab";
import RoomRemodel from "@/pages/RoomRemodel";
import CrewsManager from "@/components/CrewsManager";
import AssembliesManager from "@/components/AssembliesManager";
import { LocationPicker } from "@/components/LocationPicker";
import { regionIndexLabel } from "@/lib/regions";
import { PriceCheck } from "@/components/PriceCheck";
import SnapshotControl from "@/components/SnapshotControl";

const CSI = {
  "01": "General Requirements", "02": "Existing Conditions", "03": "Concrete", "04": "Masonry",
  "05": "Metals", "06": "Wood, Plastics & Composites", "07": "Thermal & Moisture Protection",
  "08": "Openings", "09": "Finishes", "10": "Specialties", "11": "Equipment", "12": "Furnishings",
  "21": "Fire Suppression", "22": "Plumbing", "23": "HVAC", "26": "Electrical", "31": "Earthwork",
  "32": "Exterior Improvements", "33": "Utilities",
};
const rid = (p) => `${p}_${Math.random().toString(36).slice(2, 10)}`;

function RadialScore({ score = 0 }) {
  const color = score >= 85 ? "#10B981" : score >= 70 ? "#0EA5E9" : score >= 50 ? "#F59E0B" : "#EF4444";
  const r = 34, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  return (
    <div className="relative h-24 w-24">
      <svg className="h-24 w-24 -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="7" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <span className="font-mono font-bold text-xl tabular-nums" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

const Field = ({ value, onChange, type = "text", className = "", testid, ...p }) => (
  <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
    className={`w-full bg-transparent px-2 py-1.5 rounded outline-none focus:bg-secondary focus:ring-1 focus:ring-primary/40 ${className}`} {...p} />
);

const TextArea = ({ value, onChange, className = "", testid, ...p }) => (
  <textarea data-testid={testid} rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
    className={`w-full bg-transparent px-2 py-1.5 rounded outline-none resize-y leading-snug max-h-40 overflow-y-auto focus:bg-secondary focus:ring-1 focus:ring-primary/40 ${className}`} {...p} />
);

const TABS = [
  { id: "estimate", label: "Estimate", icon: FileSpreadsheet },
  { id: "takeoff", label: "Takeoff", icon: Ruler },
  { id: "design3d", label: "3D Design", icon: Boxes },
  { id: "remodel", label: "Room Remodel", icon: ScanLine },
  { id: "schedule", label: "Schedule", icon: CalendarRange },
  { id: "risk", label: "Risk", icon: ShieldAlert },
  { id: "proposal", label: "Proposal", icon: FileText },
  { id: "changeorders", label: "Change Orders", icon: GitPullRequest },
  { id: "revisions", label: "Revisions", icon: Replace },
  { id: "tendering", label: "Tendering", icon: Gavel },
  { id: "review", label: "AI Review", icon: ClipboardCheck },
  { id: "bidreview", label: "Bid Review", icon: FileCheck2 },
  { id: "win", label: "Win %", icon: TrendingUp },
  { id: "scenarios", label: "What-If", icon: FlaskConical },
  { id: "progress", label: "Progress", icon: Camera },
  { id: "photos", label: "Photos", icon: ImageIcon },
];

function TabBar({ tab, setTab }) {
  return (
    <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto" data-testid="project-tabs">
      {TABS.map((t) => (
        <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${
            tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
          }`}>
          <t.icon className="h-4 w-4" /> {t.label}
        </button>
      ))}
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [boq, setBoq] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [scope, setScope] = useState("");
  const [gen, setGen] = useState(false);
  const [attach, setAttach] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [tab, setTab] = useState("estimate");
  const [editLoc, setEditLoc] = useState(false);
  const [locVal, setLocVal] = useState("");

  const { data: project } = useQuery({ queryKey: ["project", id], queryFn: async () => (await api.get(`/projects/${id}`)).data });
  const { data: boqData, isLoading } = useQuery({ queryKey: ["boq", id], queryFn: async () => (await api.get(`/projects/${id}/boq`)).data });

  useEffect(() => { if (boqData && !dirty) setBoq(boqData); }, [boqData]); // eslint-disable-line
  useEffect(() => { setLocVal(project?.location || ""); }, [project?.location]);
  const saveLoc = async () => {
    try {
      await api.patch(`/projects/${id}`, { location: locVal });
      qc.invalidateQueries({ queryKey: ["project", id] });
      setEditLoc(false);
      toast.success("Location saved — AI will price to this local market");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const refreshBoq = async () => {
    const { data } = await api.get(`/projects/${id}/boq`);
    setBoq(data); setDirty(false); qc.setQueryData(["boq", id], data);
  };

  const totals = useMemo(() => (boq ? computeTotals(boq) : null), [boq]);
  const crewHours = useMemo(() => (boq ? boq.sections.reduce((a, s) => a + (s.items || []).reduce((b, it) => b + (parseFloat(it.labor_hours) || 0), 0), 0) : 0), [boq]);
  const { data: crews = [] } = useQuery({ queryKey: ["crews"], queryFn: async () => (await api.get(`/crews`)).data });
  const [showCrews, setShowCrews] = useState(false);
  const [showAssemblies, setShowAssemblies] = useState(false);
  const [actuals, setActuals] = useState(false);
  const applyCrew = (si, ii, crewId) => mut((n) => {
    const it = n.sections[si].items[ii];
    const c = crews.find((x) => x.id === crewId);
    if (c) { it.crew_name = c.name; it.labor_hourly_rate = c.hourly_rate; } else { it.crew_name = ""; }
    recalcLabor(it);
  });
  const applyAssembly = (a, mult) => {
    const m = mult > 0 ? mult : 1;
    mut((n) => {
      a.items.forEach((ai) => {
        const code = ai.code || "01";
        let sec = n.sections.find((s) => s.code === code);
        if (!sec) { sec = { id: rid("sec"), code, title: CSI[code] || "General", items: [] }; n.sections.push(sec); }
        sec.items.push({ id: rid("item"), code: ai.code || "", description: ai.description, unit: ai.unit || "ea",
          quantity: +((ai.quantity || 0) * m).toFixed(2), labor_rate: ai.labor_rate || 0, material_rate: ai.material_rate || 0, equipment_rate: ai.equipment_rate || 0 });
      });
    });
    toast.success(`Added ${a.name} (${m} ${a.unit}) — ${a.items.length} line item(s)`);
  };
  const learnRates = async () => {
    if (dirty) { await save(); }
    try { const { data } = await api.post(`/boq/${boq.id}/learn-from-actuals`); toast.success(`Learned ${data.learned} rate(s) from actuals`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const applyPriceBook = async () => {
    if (dirty) { await save(); }
    try {
      const { data } = await api.post(`/projects/${id}/apply-price-book`);
      await refreshBoq();
      toast.success(`Filled ${data.filled} rate(s) from your ${data.region} price book — no AI used`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const actualsTotal = useMemo(() => (boq ? boq.sections.reduce((a, s) => a + (s.items || []).reduce((b, it) => b + (parseFloat(it.actual_cost) || 0), 0), 0) : 0), [boq]);
  const estForActuals = useMemo(() => (boq ? boq.sections.reduce((a, s) => a + (s.items || []).reduce((b, it) => b + (parseFloat(it.actual_cost) > 0 ? lineTotal(it) : 0), 0), 0) : 0), [boq]);

  const mut = (fn) => { setBoq((b) => { const n = structuredClone(b); fn(n); return n; }); setDirty(true); };
  const recalcLabor = (it) => {
    const hrs = parseFloat(it.labor_hours) || 0, rate = parseFloat(it.labor_hourly_rate) || 0, qty = parseFloat(it.quantity) || 0;
    if (hrs > 0 && rate > 0 && qty > 0) it.labor_rate = +((hrs * rate) / qty).toFixed(2);
  };
  const updateItem = (si, ii, f, v) => mut((n) => { const it = n.sections[si].items[ii]; it[f] = v; if (f === "labor_hours" || f === "labor_hourly_rate" || f === "quantity") recalcLabor(it); });
  const addItem = (si) => mut((n) => n.sections[si].items.push({ id: rid("item"), code: "", description: "", unit: "ea", quantity: 1, labor_rate: 0, material_rate: 0, equipment_rate: 0 }));
  const delItem = (si, ii) => mut((n) => n.sections[si].items.splice(ii, 1));
  const addSection = () => mut((n) => n.sections.push({ id: rid("sec"), code: "01", title: CSI["01"], items: [] }));
  const delSection = (si) => mut((n) => n.sections.splice(si, 1));
  const setSectionCode = (si, code) => mut((n) => { n.sections[si].code = code; n.sections[si].title = CSI[code] || n.sections[si].title; });
  const setMarkup = (f, v) => mut((n) => { n.markups[f] = v; });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/boq/${boq.id}`, { name: boq.name, sections: boq.sections, markups: boq.markups });
      setBoq(data); setDirty(false);
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["analytics"] });
      toast.success("BOQ saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const autoPrice = async () => {
    if (dirty) { await save(); }
    try {
      const { data } = await api.post(`/boq/${boq.id}/price`);
      setBoq(data); setDirty(false);
      toast.success(`Auto-priced ${data.priced_count} item(s) from catalog`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveRates = async () => {
    if (dirty) { await save(); }
    try {
      const { data } = await api.post(`/boq/${boq.id}/save-rates`);
      toast.success(`Saved ${data.saved} rate(s) to your Cost Catalog`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const onPickImage = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAttach({ kind: "image", name: file.name, image_b64: String(reader.result).split(",")[1], preview: reader.result });
    reader.readAsDataURL(file);
  };
  const onPickFile = async (file) => {
    if (!file) return;
    if (/^image\//.test(file.type)) { onPickImage(file); return; }
    setExtracting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/extract-text", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScope((s) => (s ? s + "\n\n" : "") + data.text);
      setAttach({ kind: "doc", name: data.filename });
      toast.success(`Added scope from ${data.filename}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setExtracting(false); }
  };
  const runGenerate = async () => {
    if (!scope.trim() && !attach?.image_b64) return;
    setGen(true);
    try {
      const { data } = await api.post(`/boq/${boq.id}/generate`, { scope, image_b64: attach?.image_b64 || null, mode: boq.sections?.length ? "append" : "replace" });
      setBoq(data); setDirty(false); setGenOpen(false); setScope(""); setAttach(null);
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success("AI generated your BOQ");
    } catch (e) { toast.error(apiErr(e)); } finally { setGen(false); }
  };

  if (isLoading || !boq) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const validation = boq.validation || { score: 0, findings: [], counts: {} };
  const sevIcon = { critical: AlertTriangle, warning: AlertTriangle, info: Info };
  const sevColor = { critical: "text-destructive", warning: "text-amber-500", info: "text-sky-400" };

  return (
    <div className="max-w-7xl mx-auto">
      <button onClick={() => navigate("/projects")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3 transition-colors" data-testid="back-to-projects">
        <ArrowLeft className="h-4 w-4" /> Projects
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{project?.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{project?.client || "—"} · <span className="capitalize">{project?.type}</span></p>
          {editLoc ? (
            <div className="flex items-center gap-2 mt-2">
              <MapPin className="h-4 w-4 text-primary shrink-0" />
              <div className="w-56">
                <LocationPicker testid="project-location-edit" value={locVal} onChange={setLocVal} autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") saveLoc(); if (e.key === "Escape") { setLocVal(project?.location || ""); setEditLoc(false); } }}
                  inputClassName="w-full rounded-lg border border-input bg-card px-2.5 py-1 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
              </div>
              <button data-testid="project-location-save" onClick={saveLoc} className="text-sm font-semibold text-primary hover:underline">Save</button>
              <button onClick={() => { setLocVal(project?.location || ""); setEditLoc(false); }} className="text-sm text-muted-foreground hover:underline">Cancel</button>
            </div>
          ) : (
            <button data-testid="project-location-display" onClick={() => setEditLoc(true)}
              className="inline-flex items-center gap-1.5 mt-2 text-sm text-muted-foreground hover:text-primary transition-colors group">
              <MapPin className="h-4 w-4" />
              <span>{project?.location || "Add project location for local rates"}</span>
              <span className="opacity-0 group-hover:opacity-100 text-xs">(edit)</span>
            </button>
          )}
        </div>
        {tab === "estimate" && (
        <div className="flex flex-wrap gap-2">
          <button data-testid="ai-generate-btn" onClick={() => setGenOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 font-bold hover:bg-orange-600 active:bg-orange-700 transition-colors">
            <Cpu className="h-4 w-4" /> AI Generate
          </button>
          <button data-testid="auto-price-btn" onClick={autoPrice}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 font-semibold hover:bg-secondary transition-colors">
            <Wand2 className="h-4 w-4" /> Auto-Price
          </button>
          <button data-testid="save-rates-btn" onClick={saveRates}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 font-semibold hover:bg-secondary transition-colors">
            <Database className="h-4 w-4" /> Save Rates
          </button>
          <button data-testid="crews-btn" onClick={() => setShowCrews(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 font-semibold hover:bg-secondary transition-colors">
            <Users className="h-4 w-4" /> Crews
          </button>
          <button data-testid="assemblies-btn" onClick={() => setShowAssemblies(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 font-semibold hover:bg-secondary transition-colors">
            <Blocks className="h-4 w-4" /> Assemblies
          </button>
          <button data-testid="apply-pricebook-btn" onClick={applyPriceBook}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 font-semibold hover:bg-secondary transition-colors">
            <BookMarked className="h-4 w-4" /> Price Book
          </button>
          <button data-testid="actuals-toggle-btn" onClick={() => setActuals((v) => !v)}
            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 font-semibold transition-colors ${actuals ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"}`}>
            <Receipt className="h-4 w-4" /> Actuals
          </button>
          {actuals && (
            <button data-testid="learn-rates-btn" onClick={learnRates}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 font-semibold hover:bg-secondary transition-colors">
              <Database className="h-4 w-4" /> Learn Rates
            </button>
          )}
          <button data-testid="save-boq-btn" onClick={save} disabled={saving || !dirty}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {dirty ? "Save" : "Saved"}
          </button>
        </div>
        )}
      </div>

      <TabBar tab={tab} setTab={setTab} />

      {tab === "schedule" && <ScheduleTab projectId={id} />}
      {tab === "takeoff" && <TakeoffTab projectId={id} />}
      {tab === "design3d" && <Design3DTab projectId={id} />}
      {tab === "remodel" && <RoomRemodel projectId={id} embedded />}
      {tab === "risk" && <RiskTab projectId={id} />}
      {tab === "proposal" && <ProposalTab projectId={id} />}
      {tab === "changeorders" && <ChangeOrdersTab projectId={id} />}
      {tab === "tendering" && <TenderingTab projectId={id} />}
      {tab === "review" && <QuoteReviewTab projectId={id} />}
      {tab === "bidreview" && <BidReviewTab projectId={id} />}
      {tab === "win" && <WinTab projectId={id} />}
      {tab === "scenarios" && <ScenariosTab projectId={id} />}
      {tab === "revisions" && <RevisionsTab projectId={id} />}
      {tab === "progress" && <ProgressTab projectId={id} />}
      {tab === "photos" && <ProjectPhotos projectId={id} />}

      <CrewsManager open={showCrews} onClose={() => setShowCrews(false)} />
      <AssembliesManager open={showAssemblies} onClose={() => setShowAssemblies(false)} onApply={applyAssembly} boq={boq} />

      {tab === "estimate" && (<>
      {/* Cost index badge + price check */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
      {(() => {
        const idx = regionIndexLabel(project?.location || "");
        const tone = idx.pct > 0 ? "border-orange-500/40 bg-orange-500/10 text-orange-500"
          : idx.pct < 0 ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500"
          : "border-border bg-secondary text-muted-foreground";
        return (
          <button data-testid="cost-index-badge" onClick={() => setEditLoc(true)}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors hover:opacity-80 ${tone}`}>
            <MapPin className="h-3.5 w-3.5" />
            {project?.location ? <span>{project.location} · {idx.text}</span> : <span>Add a location for local competitive rates</span>}
          </button>
        );
      })()}
      <PriceCheck projectId={id} dirty={dirty} onSave={save} onMatched={refreshBoq} />
      <SnapshotControl projectId={id} scope="estimate" onRestored={refreshBoq} />
      {(boq?.rate_basis || (boq?.rate_sources || []).length > 0) && (
        <span data-testid="local-rates-badge"
          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-500 px-3 py-1.5 text-xs font-semibold">
          <CheckCircle2 className="h-3.5 w-3.5" /> Local contractor rates applied
        </span>
      )}
      </div>
      {/* KPI bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          ["Grand Total", money(totals.grand_total, 0), "kpi-grand-total"],
          ["Base Subtotal", money(totals.base_subtotal, 0), "kpi-subtotal"],
          ["Line Items", boq.sections.reduce((a, s) => a + s.items.length, 0), "kpi-items"],
          ["Quality Score", validation.score, "kpi-quality"],
        ].map(([l, v, tid]) => (
          <div key={l} className="rounded-xl border border-border bg-card p-4" data-testid={tid}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{l}</div>
            <div className="text-xl sm:text-2xl font-mono font-bold tabular-nums">{v}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* BOQ table */}
        <div className="lg:col-span-2 space-y-4">
          {boq.sections.length === 0 && (
            <div className="rounded-xl border border-dashed border-border py-16 text-center">
              <Layers className="h-9 w-9 mx-auto text-muted-foreground mb-3" />
              <p className="font-semibold">Empty BOQ</p>
              <p className="text-sm text-muted-foreground mb-4">Generate with AI or add a section manually.</p>
              <div className="flex gap-2 justify-center">
                <button onClick={() => setGenOpen(true)} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 font-bold hover:bg-orange-600 transition-colors inline-flex items-center gap-2"><Cpu className="h-4 w-4" />AI Generate</button>
                <button onClick={addSection} className="rounded-lg border border-border px-4 py-2 font-semibold hover:bg-secondary transition-colors">Add section</button>
              </div>
            </div>
          )}

          {boq.sections.map((sec, si) => {
            const sub = (sec.items || []).reduce((a, it) => a + lineTotal(it), 0);
            const isCol = collapsed[sec.id];
            return (
              <div key={sec.id} className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`boq-section-${si}`}>
                <div className="flex items-center gap-3 px-4 py-3 bg-popover border-b border-border">
                  <button onClick={() => setCollapsed((c) => ({ ...c, [sec.id]: !c[sec.id] }))} className="text-muted-foreground hover:text-foreground">
                    <ChevronDown className={`h-4 w-4 transition-transform ${isCol ? "-rotate-90" : ""}`} />
                  </button>
                  <select data-testid={`section-code-${si}`} value={sec.code} onChange={(e) => setSectionCode(si, e.target.value)}
                    className="bg-secondary rounded px-2 py-1 text-xs font-mono outline-none focus:ring-1 focus:ring-primary">
                    {Object.entries(CSI).map(([k, v]) => <option key={k} value={k}>{`${k} — ${v}`}</option>)}
                  </select>
                  <span className="font-semibold text-sm flex-1 truncate">{sec.title}</span>
                  <span className="font-mono font-bold text-primary tabular-nums text-sm">{money(sub)}</span>
                  <button data-testid={`delete-section-${si}`} onClick={() => delSection(si)} className="h-7 w-7 grid place-items-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                {!isCol && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[980px]">
                      <thead>
                        <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border/60">
                          <th className="px-3 py-2 w-[26%]">Description</th>
                          <th className="px-2 py-2">Unit</th>
                          <th className="px-2 py-2 text-right">Qty</th>
                          <th className="px-2 py-2">Crew</th>
                          <th className="px-2 py-2 text-right">Crew Hrs</th>
                          <th className="px-2 py-2 text-right">$/hr</th>
                          <th className="px-2 py-2 text-right">Labor $/unit</th>
                          <th className="px-2 py-2 text-right">Material $/unit</th>
                          <th className="px-2 py-2 text-right">Equip $/unit</th>
                          <th className="px-2 py-2 text-right">Total</th>
                          {actuals && <th className="px-2 py-2 text-right">Actual $</th>}
                          {actuals && <th className="px-2 py-2 text-right">Var</th>}
                          <th className="px-2 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sec.items.map((it, ii) => (
                          <tr key={it.id} className="border-b border-border/40 hover:bg-secondary/30" data-testid={`boq-item-${si}-${ii}`}>
                            <td className="px-1 align-top"><TextArea testid={`item-desc-${si}-${ii}`} value={it.description} onChange={(v) => updateItem(si, ii, "description", v)} placeholder="Description" /></td>
                            <td className="px-1 w-16"><Field testid={`item-unit-${si}-${ii}`} value={it.unit} onChange={(v) => updateItem(si, ii, "unit", v)} className="font-mono text-xs text-center" /></td>
                            <td className="px-1 w-20"><Field testid={`item-qty-${si}-${ii}`} type="number" value={it.quantity} onChange={(v) => updateItem(si, ii, "quantity", v)} className="font-mono tabular-nums text-right" /></td>
                            <td className="px-1 w-28">
                              <select data-testid={`item-crew-${si}-${ii}`} value={crews.find((c) => c.name === it.crew_name)?.id || ""} onChange={(e) => applyCrew(si, ii, e.target.value)}
                                className="w-full rounded-md border border-transparent bg-transparent px-1.5 py-1.5 text-xs outline-none focus:border-primary hover:bg-secondary/50 cursor-pointer">
                                <option value="">— crew —</option>
                                {crews.map((c) => <option key={c.id} value={c.id}>{c.name} (${c.hourly_rate}/hr)</option>)}
                              </select>
                            </td>
                            <td className="px-1 w-20"><Field testid={`item-hours-${si}-${ii}`} type="number" value={it.labor_hours} onChange={(v) => updateItem(si, ii, "labor_hours", v)} className="font-mono tabular-nums text-right" placeholder="—" /></td>
                            <td className="px-1 w-20"><Field testid={`item-hourly-${si}-${ii}`} type="number" value={it.labor_hourly_rate} onChange={(v) => updateItem(si, ii, "labor_hourly_rate", v)} className="font-mono tabular-nums text-right" placeholder="—" /></td>
                            <td className="px-1 w-24"><Field testid={`item-labor-${si}-${ii}`} type="number" value={it.labor_rate} onChange={(v) => updateItem(si, ii, "labor_rate", v)} className="font-mono tabular-nums text-right" /></td>
                            <td className="px-1 w-24"><Field testid={`item-material-${si}-${ii}`} type="number" value={it.material_rate} onChange={(v) => updateItem(si, ii, "material_rate", v)} className="font-mono tabular-nums text-right" /></td>
                            <td className="px-1 w-24"><Field testid={`item-equip-${si}-${ii}`} type="number" value={it.equipment_rate} onChange={(v) => updateItem(si, ii, "equipment_rate", v)} className="font-mono tabular-nums text-right" /></td>
                            <td className="px-2 text-right font-mono font-semibold tabular-nums whitespace-nowrap">{money(lineTotal(it))}</td>
                            {actuals && <td className="px-1 w-24"><Field testid={`item-actual-${si}-${ii}`} type="number" value={it.actual_cost} onChange={(v) => updateItem(si, ii, "actual_cost", v)} className="font-mono tabular-nums text-right" placeholder="—" /></td>}
                            {actuals && (() => { const a = parseFloat(it.actual_cost) || 0; const est = lineTotal(it); const d = a - est; const vc = !a ? "text-muted-foreground" : d > 0 ? "text-destructive" : "text-emerald-500"; return <td className={`px-2 text-right font-mono text-xs tabular-nums whitespace-nowrap ${vc}`}>{a ? `${d > 0 ? "+" : ""}${money(d)}` : "—"}</td>; })()}
                            <td className="px-1 w-8"><button data-testid={`delete-item-${si}-${ii}`} onClick={() => delItem(si, ii)} className="h-6 w-6 grid place-items-center rounded text-muted-foreground hover:text-destructive"><X className="h-3.5 w-3.5" /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button data-testid={`add-item-${si}`} onClick={() => addItem(si)} className="w-full flex items-center justify-center gap-1.5 py-2.5 text-sm text-muted-foreground hover:text-primary hover:bg-secondary/40 transition-colors border-t border-border/60">
                      <Plus className="h-3.5 w-3.5" /> Add line item
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {boq.sections.length > 0 && (
            <button data-testid="add-section-btn" onClick={addSection} className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-border py-3 text-sm font-semibold text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors">
              <Plus className="h-4 w-4" /> Add CSI section
            </button>
          )}
        </div>

        {/* Right rail: markups + validation */}
        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-5" data-testid="markups-panel">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Markups & Totals</h3>
            <div className="space-y-3">
              {[["overhead", "Overhead"], ["profit", "Profit"], ["contingency", "Contingency"], ["tax", "Tax / VAT"]].map(([k, l]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <label className="text-sm text-muted-foreground">{l}</label>
                  <div className="flex items-center gap-1">
                    <input data-testid={`markup-${k}`} type="number" value={boq.markups[k] ?? 0} onChange={(e) => setMarkup(k, e.target.value)}
                      className="w-20 rounded border border-input bg-popover px-2 py-1 text-right font-mono tabular-nums outline-none focus:border-primary" />
                    <span className="text-muted-foreground text-sm">%</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-border space-y-1.5 text-sm">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground pb-1">Direct cost by type</div>
              {[["Labor" + (crewHours ? ` (${num(crewHours, 1)} hrs)` : ""), totals.labor_total], ["Material", totals.material_total], ["Equipment", totals.equipment_total]].map(([l, v]) => (
                <div key={l} className="flex justify-between" data-testid={`cost-type-${String(l).toLowerCase().startsWith("labor") ? "labor" : l.toLowerCase()}`}><span className="text-muted-foreground">{l}</span><span className="font-mono tabular-nums">{money(v)}</span></div>
              ))}
              <div className="pt-2 mt-1 border-t border-border/60 space-y-1.5">
              {[["Base subtotal", totals.base_subtotal], ["Overhead", totals.overhead_amount], ["Profit", totals.profit_amount], ["Contingency", totals.contingency_amount], ["Tax", totals.tax_amount]].map(([l, v]) => (
                <div key={l} className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="font-mono tabular-nums">{money(v)}</span></div>
              ))}
              </div>
              <div className="flex justify-between pt-2 mt-2 border-t border-border">
                <span className="font-bold">Grand Total</span>
                <span className="font-mono font-bold text-primary tabular-nums text-lg" data-testid="grand-total-value">{money(totals.grand_total)}</span>
              </div>
            </div>
          </div>

          {actualsTotal > 0 && (
            <div className="rounded-xl border border-border bg-card p-5" data-testid="variance-panel">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Receipt className="h-4 w-4 text-primary" /> Bid vs. Actual</h3>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Estimated (tracked lines)</span><span className="font-mono tabular-nums">{money(estForActuals)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Actual to date</span><span className="font-mono tabular-nums">{money(actualsTotal)}</span></div>
                <div className="flex justify-between pt-2 mt-1 border-t border-border">
                  <span className="font-bold">Variance</span>
                  <span className={`font-mono font-bold tabular-nums ${actualsTotal - estForActuals > 0 ? "text-destructive" : "text-emerald-500"}`}>
                    {actualsTotal - estForActuals > 0 ? "+" : ""}{money(actualsTotal - estForActuals)}
                    {estForActuals > 0 ? ` (${(((actualsTotal - estForActuals) / estForActuals) * 100).toFixed(1)}%)` : ""}
                  </span>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">Use "Learn Rates" to feed these actuals back into your Cost Catalog so future estimates start from real numbers.</p>
            </div>
          )}


          <div className="rounded-xl border border-border bg-card p-5" data-testid="validation-panel">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Validation</h3>
            <div className="flex items-center gap-4 mb-4">
              <RadialScore score={validation.score} />
              <div className="text-sm">
                <div className="capitalize font-semibold">{validation.rating || "—"}</div>
                <div className="text-muted-foreground text-xs mt-1">
                  {validation.counts?.critical || 0} critical · {validation.counts?.warning || 0} warnings · {validation.counts?.info || 0} info
                </div>
                {dirty && <div className="text-[11px] text-amber-500 mt-1">Save to refresh score</div>}
              </div>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {(validation.findings || []).length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-emerald-500"><CheckCircle2 className="h-4 w-4" /> No issues found.</div>
              ) : validation.findings.map((f, i) => {
                const Icon = sevIcon[f.severity] || Info;
                return (
                  <div key={i} className="flex items-start gap-2 text-sm" data-testid={`finding-${i}`}>
                    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${sevColor[f.severity]}`} />
                    <span className="text-muted-foreground">{f.message}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {(boq?.rate_basis || (boq?.rate_sources || []).length > 0) && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="rate-basis-card">
              <div className="flex items-center gap-2 mb-1.5">
                <MapPin className="h-4 w-4 text-emerald-500" />
                <h3 className="font-semibold text-sm">Real-time rate basis</h3>
              </div>
              {boq.rate_basis && <p className="text-xs text-muted-foreground mb-2">{boq.rate_basis}</p>}
              {(boq.rate_sources || []).length > 0 && (
                <div className="space-y-1" data-testid="rate-sources">
                  {boq.rate_sources.map((s, i) => (
                    <a key={i} href={s.url} target="_blank" rel="noreferrer" data-testid={`rate-source-${i}`}
                      className="block text-xs text-primary truncate hover:underline">↗ {s.label || s.url}</a>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-muted-foreground mt-2">Rates sourced from live web data for the project location. Verify against your own quotes.</p>
            </div>
          )}

          <CompliancePanel projectId={id} />

        </div>
      </div>
      </>)}

      {/* AI Generate modal */}
      {genOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !gen && setGenOpen(false)}>
          <div className="w-full max-w-lg rounded-xl border border-border bg-popover overflow-hidden animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="ai-generate-modal">
            <div className="relative px-6 py-5 border-b border-border blueprint-grid">
              <div className="relative flex items-center gap-2">
                <Cpu className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-bold">AI BOQ Generator</h2>
              </div>
              <p className="relative text-sm text-muted-foreground mt-1">Describe the scope of work — GPT-5.6 drafts CSI-classified, priced line items.</p>
            </div>
            <div className="p-6">
              <textarea data-testid="scope-input" value={scope} onChange={(e) => setScope(e.target.value)} rows={6} disabled={gen}
                placeholder="e.g. Renovate a 400 sf office bathroom: demolish fixtures, new plumbing for two toilets and two lavatories, porcelain tile, LED lighting, paint, tankless water heater."
                className="w-full rounded-lg border border-input bg-card px-3 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 resize-none text-sm" />

              <div className="flex flex-wrap items-center gap-2 mt-3">
                <label data-testid="btn-take-photo" className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary cursor-pointer">
                  <Camera className="h-3.5 w-3.5" /> Take photo
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onPickImage(e.target.files?.[0])} />
                </label>
                <label data-testid="btn-upload-image" className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary cursor-pointer">
                  <ImageIcon className="h-3.5 w-3.5" /> Upload image
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onPickImage(e.target.files?.[0])} />
                </label>
                <label data-testid="btn-upload-file" className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary cursor-pointer">
                  {extracting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />} Upload file
                  <input type="file" accept=".pdf,.doc,.docx,.txt,.csv,.md,image/*" className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} />
                </label>
              </div>
              {attach && (
                <div className="flex items-center gap-3 mt-3 rounded-lg border border-border bg-card p-2" data-testid="attach-preview">
                  {attach.preview
                    ? <img src={attach.preview} alt="" className="h-12 w-12 rounded object-cover" />
                    : <FileText className="h-6 w-6 text-primary mx-1.5" />}
                  <span className="text-xs text-muted-foreground flex-1 truncate">{attach.kind === "image" ? "Photo will be analyzed by AI" : "Scope added from"} · {attach.name}</span>
                  <button data-testid="attach-remove" onClick={() => setAttach(null)} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
                </div>
              )}

              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setGenOpen(false)} disabled={gen} className="rounded-lg border border-border px-4 py-2 font-semibold hover:bg-secondary transition-colors">Cancel</button>
                <button data-testid="run-generate-btn" onClick={runGenerate} disabled={gen || (!scope.trim() && !attach?.image_b64)}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-5 py-2 font-bold hover:bg-orange-600 transition-colors disabled:opacity-60">
                  {gen ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4" /> Generate</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
